import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  TrendingUp, 
  BarChart3, 
  Target, 
  Users, 
  Award, 
  Play,
  Twitter,
  Instagram,
  Send,
  MessageCircle,
  ExternalLink,
  LineChart,
  PieChart,
  Activity,
  DollarSign,
  X,
  Sparkles,
  Clock,
  Gift,
  Youtube,
  Check,
  Calendar,
  Monitor,
  Search,
  Menu
} from 'lucide-react';
import PaymentModal from './PaymentModal';
import DailyTraderQuote from './DailyTraderQuote';
import AITrackRecordSection from './AITrackRecordSection';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingPage = () => {
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    subscriptionType: '',
    packageDetails: null,
    appliedCoupon: null  // For auto-applying coupon
  });

  // Active offers state
  const [activeOffers, setActiveOffers] = useState([]);
  const [showOfferBanner, setShowOfferBanner] = useState(true);

  // Dynamic pricing — sensible defaults match current production prices so mobile users
  // never see "₹..." during slow network. localStorage caches the most recent successful
  // fetch so returning visitors see the latest price instantly. Background poll keeps it fresh.
  const DEFAULT_PRICES = {
    options_only: 3500,
    complete_package: 8000,
    equity_only: 4000,
    options_only_3month: 9450,
    complete_package_3month: 19125,
    equity_only_3month: 10500,
    workshop: 4999,
    index_options_educational: 2500,
    automated_alerts_1day: 250,
    automated_alerts_3day: 550,
    automated_alerts_5day: 800,
  };

  const [servicePrices, setServicePrices] = useState(() => {
    try {
      const cached = localStorage.getItem('ucs_last_prices');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') return { ...DEFAULT_PRICES, ...parsed };
      }
    } catch { /* localStorage disabled — fall back to defaults */ }
    return DEFAULT_PRICES;
  });

  const [enabledServices, setEnabledServices] = useState({
    options_only: true,
    complete_package: true,
    equity_only: true,
    workshop: true,
    index_options_educational: true,
    automated_alerts_1day: true,
    automated_alerts_3day: true,
    automated_alerts_5day: true
  });

  const [mentorshipLink, setMentorshipLink] = useState(
    "https://www.upsurge.club/live-mentorship/tmp-by-chirag-rathod?invite=CHIR-RTHD"
  );

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch dynamic pricing and service status (cache-busted, polled so admin changes appear without reload)
  useEffect(() => {
    const fetchPricingAndServices = async () => {
      try {
        const bust = `?_t=${Date.now()}`;
        const fetchOpts = { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } };

        // Fetch prices
        const priceResponse = await fetch(`${API_URL}/api/admin/prices${bust}`, fetchOpts);
        const priceData = await priceResponse.json();
        if (priceData.prices) {
          // Merge live values over the defaults/cache so a partial response still works
          const merged = { ...(servicePrices || {}), ...priceData.prices };
          setServicePrices(merged);
          // Persist for instant render on the next visit (especially mobile)
          try { localStorage.setItem('ucs_last_prices', JSON.stringify(priceData.prices)); } catch { /* noop */ }
        }

        // Fetch enabled services
        const servicesResponse = await fetch(`${API_URL}/api/admin/services${bust}`, fetchOpts);
        const servicesData = await servicesResponse.json();
        if (servicesData.services) {
          setEnabledServices(servicesData.services);
        }

        // Fetch mentorship link
        const mentorRes = await fetch(`${API_URL}/api/mentorship-link${bust}`, fetchOpts);
        const mentorData = await mentorRes.json();
        if (mentorData.link) {
          setMentorshipLink(mentorData.link);
        }
      } catch (error) {
        console.log('Failed to fetch pricing/services:', error);
      }
    };

    fetchPricingAndServices();
    const id = setInterval(fetchPricingAndServices, 60000);
    return () => clearInterval(id);
  }, []);

  // Track visitor on page load
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        // Extract UTM parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = {
          utm_source: urlParams.get('utm_source'),
          utm_medium: urlParams.get('utm_medium'),
          utm_campaign: urlParams.get('utm_campaign'),
          utm_term: urlParams.get('utm_term'),
          utm_content: urlParams.get('utm_content')
        };

        await fetch(`${API_URL}/api/track-visitor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pageUrl: window.location.href,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            deviceType: window.innerWidth <= 768 ? 'mobile' : 'desktop',
            sessionId: sessionStorage.getItem('session_id') || Date.now().toString(),
            ...utmParams
          })
        });
      } catch (error) {
        console.log('Visitor tracking failed:', error);
      }
    };
    
    trackVisitor();
  }, []);

  // Fetch active offers (cache-busted, polled so coupon counter stays fresh)
  useEffect(() => {
    const fetchActiveOffers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/offers/active?_t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          setActiveOffers(data.offers || []);
        }
      } catch (error) {
        console.log('Failed to fetch offers:', error);
      }
    };

    fetchActiveOffers();
    // Refresh every 30s so "X left" counter on the banner / pricing cards stays accurate as payments come in
    const id = setInterval(fetchActiveOffers, 30000);
    return () => clearInterval(id);
  }, []);

  // Scroll to section if URL contains a hash (e.g. /welcome#services)
  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    // Wait for sections to mount before scrolling
    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  // Get offer for a specific subscription type
  const getOfferForSubscription = (subscriptionType) => {
    return activeOffers.find(offer => 
      offer.applicable_subscription_types?.includes(subscriptionType)
    );
  };

  // Handle subscribe with auto-apply coupon
  const handleSubscribeWithOffer = (subscriptionType, offer) => {
    const pkgData = packageData[subscriptionType];
    if (!pkgData || !pkgData.enabled) return;
    
    setPaymentModal({
      isOpen: true,
      subscriptionType,
      packageDetails: pkgData,
      appliedCoupon: offer?.code || null
    });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Guard against initial null state. Cards using these values show "…" until the live fetch resolves.
  // Guard against initial null state. With cached defaults we should always have prices.
  const pricesLoaded = !!servicePrices && Object.keys(servicePrices).length > 0;
  const safePrices = servicePrices || {};
  // Format a price. We always have a default so the dash is just a safety net.
  const fmtPrice = (val) => (val == null ? '—' : Number(val).toLocaleString('en-IN'));

  const packageData = {
    options_only: {
      name: "Options Only Package",
      amount: safePrices.options_only,
      description: "In-depth analysis, identifying stocks with Strike, entry exit levels exclusively for Options trading—perfect for high-leverage plays.",
      enabled: enabledServices.options_only,
      duration: "1 Month"
    },
    complete_package: {
      name: "Complete Package",
      amount: safePrices.complete_package,
      description: "Comprehensive coverage across Options, Futures, and Equity for a holistic trading edge. Identifying stocks with Charts, entry exit levels.",
      enabled: enabledServices.complete_package,
      duration: "1 Month"
    },
    equity_only: {
      name: "Equity Only Package", 
      amount: safePrices.equity_only,
      description: "Focused stock picks with precise entry, targets and SL, and momentum insights for Equity/Cash trading.",
      enabled: enabledServices.equity_only,
      duration: "1 Month"
    },
    options_only_3month: {
      name: "Options Only Package - 3 Months",
      amount: safePrices.options_only_3month,
      description: "In-depth analysis for 3 months with Strike, entry exit levels exclusively for Options trading.",
      enabled: enabledServices.options_only,
      duration: "3 Months"
    },
    complete_package_3month: {
      name: "Complete Package - 3 Months",
      amount: safePrices.complete_package_3month,
      description: "Comprehensive 3-month coverage across Options, Futures, and Equity with Charts and entry exit levels.",
      enabled: enabledServices.complete_package,
      duration: "3 Months"
    },
    equity_only_3month: {
      name: "Equity Only Package - 3 Months", 
      amount: safePrices.equity_only_3month,
      description: "Focused 3-month stock picks with precise entry, targets and SL for Equity/Cash trading.",
      enabled: enabledServices.equity_only,
      duration: "3 Months"
    },
    automated_alerts_1day: {
      name: "Automated Alerts - 1 Day",
      amount: safePrices.automated_alerts_1day,
      description: "85% Accuracy – Real-time Trading Signals, Premium Insights for 1 trading day.",
      enabled: enabledServices.automated_alerts_1day
    },
    automated_alerts_3day: {
      name: "Automated Alerts - 3 Days", 
      amount: safePrices.automated_alerts_3day,
      description: "85% Accuracy – Real-time Trading Signals, Premium Insights for 3 trading days.",
      enabled: enabledServices.automated_alerts_3day
    },
    automated_alerts_5day: {
      name: "Automated Alerts - 5 Days",
      amount: safePrices.automated_alerts_5day,
      description: "85% Accuracy – Real-time Trading Signals, Premium Insights for 5 trading days.",
      enabled: enabledServices.automated_alerts_5day
    },
    workshop: {
      name: "Complete Setup on Intraday Option Trading in Stocks",
      amount: safePrices.workshop,
      description: "This on-demand workshop breaks down my battle-tested strategies—from stock selection and entry/exit rules to risk management.",
      enabled: enabledServices.workshop,
      learningOutcomes: [
        "Intraday Stock Options Setup",
        "Precise entry, exit, target and SL",
        "Powerful Scanner identifying stocks for intraday options",
        "Lifetime access to Learning Videos",
        "Indicators Used for the Options Setup"
      ]
    },
    index_options_educational: {
      name: "Index Options (Educational Purpose Only)",
      amount: safePrices.index_options_educational,
      description: "Monthly educational sessions on Index Options using past market data — strictly for learning purposes.",
      enabled: enabledServices.index_options_educational,
      duration: "1 Month"
    }
  };

  const handleSubscribe = (subscriptionType, couponCode = null) => {
    // Block subscribe until prices have actually loaded — prevents NaN-amount payment links on slow networks
    if (!pricesLoaded) {
      alert('Loading latest prices… please try again in a moment.');
      return;
    }
    setPaymentModal({
      isOpen: true,
      subscriptionType,
      packageDetails: packageData[subscriptionType],
      appliedCoupon: couponCode
    });
  };

  const closePaymentModal = () => {
    setPaymentModal({
      isOpen: false,
      subscriptionType: '',
      packageDetails: null,
      appliedCoupon: null
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Floating Offer Banner */}
      {activeOffers.length > 0 && showOfferBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 text-black">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 overflow-x-auto">
                <Sparkles className="w-5 h-5 flex-shrink-0 animate-pulse" />
                <div className="flex items-center gap-6">
                  {activeOffers.slice(0, 2).map((offer, index) => (
                    <div key={offer.id || index} className="flex items-center gap-3 whitespace-nowrap">
                      <span className="font-bold">{offer.offer_title || 'Special Offer!'}</span>
                      <span className="bg-black text-yellow-400 px-2 py-0.5 rounded text-sm font-mono">
                        {offer.code}
                      </span>
                      <span className="text-sm font-semibold">{offer.discount_text}</span>
                      {offer.remaining_slots && (
                        <span className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold animate-pulse">
                          Only {offer.remaining_slots} left!
                        </span>
                      )}
                      <Button 
                        size="sm" 
                        className="bg-black text-yellow-400 hover:bg-gray-900 h-7 text-xs"
                        onClick={() => {
                          const targetType = offer.applicable_subscription_types?.[0];
                          if (targetType) {
                            handleSubscribe(targetType, offer.code);
                          } else {
                            scrollToSection('services');
                          }
                        }}
                      >
                        Claim Now
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <button 
                onClick={() => setShowOfferBanner(false)}
                className="p-1 hover:bg-black/20 rounded ml-2 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`fixed ${activeOffers.length > 0 && showOfferBanner ? 'top-10' : 'top-0'} w-full bg-black/90 backdrop-blur-sm z-50 border-b border-golden transition-all duration-300`}>
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg" 
              alt="Upper Circuit Stocks Logo" 
              className="h-12 w-auto"
            />
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            <button onClick={() => scrollToSection('about')} className="text-golden hover:text-golden-light transition-colors">About</button>
            <button onClick={() => scrollToSection('mentorship')} className="text-golden hover:text-golden-light transition-colors">Mentorship</button>
            <a href="/courses" className="text-golden hover:text-golden-light transition-colors" data-testid="nav-courses-link">Courses</a>
            <a href="/free-learning" className="text-golden hover:text-golden-light transition-colors" data-testid="nav-free-learning-link">Free Learning</a>
            <a href="/market-intelligence" className="text-golden hover:text-golden-light transition-colors" data-testid="nav-market-intelligence-link">Market Intelligence</a>
            <a href="/masterclass" className="text-golden hover:text-golden-light transition-colors" data-testid="nav-masterclass-link">Masterclass</a>
            <a href="/workshop" className="text-golden hover:text-golden-light transition-colors" data-testid="nav-workshop-link">Workshop</a>
            <button onClick={() => scrollToSection('services')} className="text-golden hover:text-golden-light transition-colors">Services</button>
            <button onClick={() => scrollToSection('contact')} className="text-golden hover:text-golden-light transition-colors">Connect</button>
            <a 
              href="/login" 
              className="bg-golden hover:bg-golden/90 text-black font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                <polyline points="10 17 15 12 10 7"/>
                <line x1="15" y1="12" x2="3" y2="12"/>
              </svg>
              Login
            </a>
          </nav>
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <a 
              href="/login" 
              className="bg-golden hover:bg-golden/90 text-black font-semibold px-3 py-1.5 rounded-lg transition-colors text-sm"
            >
              Login
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-golden hover:bg-golden/10 rounded-lg transition-colors"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 border-t border-golden/20 px-4 py-3 space-y-1" data-testid="mobile-menu">
            <button onClick={() => { scrollToSection('about'); setMobileMenuOpen(false); }} className="block w-full text-left text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors">About</button>
            <button onClick={() => { scrollToSection('mentorship'); setMobileMenuOpen(false); }} className="block w-full text-left text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors">Mentorship</button>
            <a href="/courses" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors">Courses</a>
            <a href="/free-learning" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors">Free Learning</a>
            <a href="/" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors" data-testid="mobile-nav-market-intelligence-link">Market Intelligence</a>
            <a href="/masterclass" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors" data-testid="mobile-nav-masterclass-link">Masterclass</a>
            <a href="/workshop" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors" data-testid="mobile-nav-workshop-link">Workshop</a>
            <button onClick={() => { scrollToSection('services'); setMobileMenuOpen(false); }} className="block w-full text-left text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors">Services</button>
            <button onClick={() => { scrollToSection('contact'); setMobileMenuOpen(false); }} className="block w-full text-left text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm transition-colors">Connect</button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className={`${activeOffers.length > 0 && showOfferBanner ? 'pt-28' : 'pt-20'} pb-16 px-4 relative overflow-hidden min-h-screen flex items-center transition-all duration-300`}>
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-golden/10 via-transparent to-golden/5"></div>
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-golden/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-golden/3 rounded-full blur-3xl"></div>
        
        {/* Stock Market Icons Background */}
        <div className="absolute inset-0 opacity-10">
          <TrendingUp className="absolute top-20 left-20 w-16 h-16 text-golden animate-float" />
          <BarChart3 className="absolute top-40 right-32 w-12 h-12 text-golden animate-pulse-gold" />
          <LineChart className="absolute bottom-40 left-32 w-14 h-14 text-golden animate-float" style={{animationDelay: '2s'}} />
          <DollarSign className="absolute bottom-20 right-20 w-10 h-10 text-golden animate-pulse-gold" style={{animationDelay: '1s'}} />
          <Activity className="absolute top-1/2 left-10 w-8 h-8 text-golden animate-float" style={{animationDelay: '3s'}} />
          <PieChart className="absolute top-1/3 right-10 w-12 h-12 text-golden animate-pulse-gold" style={{animationDelay: '1.5s'}} />
        </div>

        <div className="container mx-auto text-center relative z-10">
          {/* Channel Branding */}
          <div className="mb-12">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg" 
                alt="Upper Circuit Stocks Logo" 
                className="h-24 md:h-32 w-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300"
              />
            </div>
            
            {/* Main Brand Title */}
            <div className="mb-6">
              <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-golden mb-4 leading-tight">
                UPPER CIRCUIT STOCKS
              </h1>
            </div>
            
            {/* Elegant curved lines with "BY CHIRAG RATHOD" */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 mb-8 flex-wrap">
              <svg width="60" height="14" viewBox="0 0 120 20" className="text-golden sm:w-[120px] sm:h-[20px]">
                <path d="M0 10 Q30 2 60 10 T120 10" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
              <span className="text-golden font-bold text-base sm:text-xl md:text-2xl px-2 sm:px-4 whitespace-nowrap">BY CHIRAG RATHOD</span>
              <svg width="60" height="14" viewBox="0 0 120 20" className="text-golden sm:w-[120px] sm:h-[20px]">
                <path d="M0 10 Q30 18 60 10 T120 10" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </div>
          </div>
          
          <Badge className="bg-golden text-black font-semibold px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-xl mb-8 hover:bg-golden-light transition-colors leading-snug">
            Unlock Your Trading Potential with Expert Guidance
          </Badge>
          
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
            Master the Markets with<br className="hidden md:block" />
            Precision and Confidence
          </h2>
          
          <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto mb-8 leading-relaxed">
            Whether you&apos;re spotting reversals or riding momentum, strategies developed by us turns market chaos into profitable opportunities.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button 
              onClick={() => scrollToSection('services')} 
              size="lg" 
              className="bg-golden text-black hover:bg-golden-light transition-all duration-300 transform hover:scale-105 px-8 py-3 text-lg font-semibold"
            >
              Get Started Today
              <TrendingUp className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => scrollToSection('workshop')} 
              variant="outline" 
              size="lg"
              className="border-golden text-golden hover:bg-golden hover:text-black transition-all duration-300 px-8 py-3 text-lg"
            >
              View Workshop
              <Play className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-golden">7+</div>
              <div className="text-gray-400">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-golden">10,000+</div>
              <div className="text-gray-400">Students Trained</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-golden">25,000+</div>
              <div className="text-gray-400">Traders Mentored</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-4 bg-gray-900/30">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">About Me</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Proven Expertise in the Indian Stock Market</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                With over <span className="text-golden font-semibold">7 years of hands-on experience</span> navigating the dynamic Indian Stock Market, we have honed skills to deliver consistent results. We have successfully trained more than <span className="text-golden font-semibold">10,000 students</span> in advanced Option Trading techniques and mentored over <span className="text-golden font-semibold">25,000 traders</span> in their daily journeys—covering Options, Futures, and Equity/Cash Swing Trading.
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                Our specialty is to <span className="text-golden font-semibold">Identify Stocks which has potential to reach Upper Circuit on same day</span>, and also <span className="text-golden font-semibold">Intraday Options Trading</span>, where we excels at stock selection for explosive momentum in short timeframes.
              </p>
              
              <div className="bg-black/50 p-6 rounded-lg border border-golden/20">
                <h3 className="text-xl font-semibold text-golden mb-4">Proprietary Setups:</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center"><Target className="w-4 h-4 text-golden mr-2" />Intraday Stock Option Trading</li>
                  <li className="flex items-center"><BarChart3 className="w-4 h-4 text-golden mr-2" />Swing Trading in Futures and Equity</li>
                </ul>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-colors">
                <CardContent className="p-6 text-center">
                  <Award className="w-8 h-8 text-golden mx-auto mb-2" />
                  <div className="text-2xl font-bold text-golden">MBA</div>
                  <div className="text-gray-400">Finance</div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-colors">
                <CardContent className="p-6 text-center">
                  <Badge className="bg-golden/10 text-golden border-golden/20 mb-2">NISM</Badge>
                  <div className="text-sm text-gray-400">Research & Technical Analyst</div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-colors">
                <CardContent className="p-6 text-center">
                  <Users className="w-8 h-8 text-golden mx-auto mb-2" />
                  <div className="text-2xl font-bold text-golden">10,000+</div>
                  <div className="text-gray-400">Students Trained</div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-colors">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="w-8 h-8 text-golden mx-auto mb-2" />
                  <div className="text-2xl font-bold text-golden">25,000+</div>
                  <div className="text-gray-400">Traders Mentored</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Trader Quote (AI-curated, refreshes at IST midnight) */}
      <DailyTraderQuote />

      {/* Live Mentorship Program Section */}
      <section id="mentorship" className="py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-golden/5 via-black to-golden/5"></div>
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-10">
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mb-4 animate-pulse text-sm px-3 py-1">
              LIMITED SEATS
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Trading <span className="text-golden">Mentorship</span> Program
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto">
              by Chirag Rathod <span className="text-gray-500">in association with</span> <span className="text-blue-400">Upsurge.club</span>
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-5 gap-0 rounded-2xl overflow-hidden border border-golden/30 bg-gray-950" data-testid="mentorship-card">
              {/* Left: Premium CSS Design Panel */}
              <div className="md:col-span-2 relative p-6 md:p-8 flex flex-col justify-between" style={{background: 'linear-gradient(160deg, #1a1400 0%, #0d0800 40%, #1a1200 100%)'}}>
                <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(212,175,55,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(212,175,55,0.3) 0%, transparent 50%)'}}></div>
                <div className="relative z-10 flex flex-col h-full justify-between">
                  {/* Program Title */}
                  <div className="mb-6">
                    <p className="text-golden/70 text-xs font-semibold tracking-[0.3em] uppercase mb-2">Live Program</p>
                    <h3 className="text-2xl md:text-3xl font-bold leading-tight">
                      <span className="text-golden" style={{fontFamily: 'Georgia, serif'}}>Trading</span><br />
                      <span className="text-white" style={{fontFamily: 'Georgia, serif'}}>Mentorship</span><br />
                      <span className="text-golden italic text-xl" style={{fontFamily: 'Georgia, serif'}}>Program</span>
                    </h3>
                    <div className="w-16 h-0.5 bg-gradient-to-r from-golden to-transparent mt-3 mb-3"></div>
                    <p className="text-gray-400 text-xs">by <span className="text-white font-medium">Chirag Rathod</span></p>
                    <p className="text-gray-500 text-xs">in association with <span className="text-blue-400 font-medium">Upsurge.club</span></p>
                  </div>

                  {/* Key Highlights */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 bg-golden/5 border border-golden/15 rounded-lg px-3 py-2.5">
                      <div className="w-8 h-8 rounded-full bg-golden/20 flex items-center justify-center flex-shrink-0">
                        <Monitor className="w-4 h-4 text-golden" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">12 Live Sessions</p>
                        <p className="text-golden/60 text-[10px] uppercase tracking-wider">Learn. Trade. Grow.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-golden/5 border border-golden/15 rounded-lg px-3 py-2.5">
                      <div className="w-8 h-8 rounded-full bg-golden/20 flex items-center justify-center flex-shrink-0">
                        <Search className="w-4 h-4 text-golden" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">6+ Stock Scanners</p>
                        <p className="text-golden/60 text-[10px] uppercase tracking-wider">Spot Opportunities Faster</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-golden/5 border border-golden/15 rounded-lg px-3 py-2.5">
                      <div className="w-8 h-8 rounded-full bg-golden/20 flex items-center justify-center flex-shrink-0">
                        <Send className="w-4 h-4 text-golden" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">VIP Telegram</p>
                        <p className="text-golden/60 text-[10px] uppercase tracking-wider">Premium Community</p>
                      </div>
                    </div>
                  </div>

                  {/* Schedule Badge */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="bg-golden/15 border border-golden/25 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-golden" />
                      <span className="text-golden text-xs font-semibold">3rd August, 2026</span>
                    </div>
                    <div className="bg-golden/15 border border-golden/25 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-golden" />
                      <span className="text-golden text-xs font-semibold">10:00 - 11:00 AM</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Details */}
              <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-between">
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-golden/10 border border-golden/20 rounded-lg p-3 text-center">
                    <p className="text-golden text-xl font-bold">12</p>
                    <p className="text-gray-400 text-xs">Live Sessions</p>
                  </div>
                  <div className="bg-golden/10 border border-golden/20 rounded-lg p-3 text-center">
                    <p className="text-golden text-xl font-bold">6+</p>
                    <p className="text-gray-400 text-xs">Stock Scanners</p>
                  </div>
                  <div className="bg-golden/10 border border-golden/20 rounded-lg p-3 text-center">
                    <p className="text-golden text-xl font-bold">VIP</p>
                    <p className="text-gray-400 text-xs">Telegram Group</p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2.5 mb-6" data-testid="mentorship-features">
                  {[
                    "Build a strong foundation in price action, structure & trend identification",
                    "Create a repeatable trading system for intraday and swing trading",
                    "Trade confidently across stocks, commodities, and crypto markets",
                    "Master risk-to-reward strategies for consistency and profitability",
                    "Develop strong risk management to protect capital",
                    "Create your own rule-based, scalable trading system"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-golden mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm leading-tight">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Price & CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-3xl font-bold text-white">₹11,999<span className="text-gray-500 text-lg font-normal">/-</span></p>
                      <p className="text-gray-500 text-sm line-through">₹23,999</p>
                      <span className="text-xs font-bold bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full border border-green-500/30">50% OFF</span>
                    </div>
                    <p className="text-xs text-green-400 mt-1.5">Use coupon <span className="font-bold bg-green-400/10 px-1.5 py-0.5 rounded">CHRG10</span> to get it for <span className="font-bold text-green-300">₹9,999</span> (first 25 only)</p>
                  </div>
                  <a
                    href={mentorshipLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0"
                    data-testid="mentorship-enroll-btn"
                  >
                    <Button className="bg-golden hover:bg-golden/90 text-black font-bold px-8 py-5 text-base transition-all duration-300 transform hover:scale-105">
                      Enroll Now
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stock Market Courses Section */}
      <section id="courses" className="py-16 px-4 bg-gradient-to-b from-black/50 to-gray-900/30">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">
              <Play className="w-3 h-3 mr-1 inline" /> Learn & Earn
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Stock Market <span className="text-golden">Courses</span>
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto">
              Master trading with comprehensive video courses. Learn at your own pace with lifetime access.
            </p>
          </div>
          <div className="flex justify-center">
            <a
              href="/courses"
              className="inline-flex items-center gap-2 bg-golden hover:bg-golden/90 text-black font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105"
              data-testid="landing-view-courses-btn"
            >
              <Play className="w-5 h-5" />
              View All Courses
              <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">Services</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Tailored Insights for Every Trader</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Empower your decisions with daily, actionable educational insights delivered straight to you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Package 1 - Options Only */}
            {enabledServices.options_only && (
            <Card className="bg-gradient-to-b from-black/80 to-gray-900/80 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-golden to-golden-light"></div>
              {/* Offer Badge */}
              {getOfferForSubscription('options_only') && (
                <div className="absolute top-3 right-3 z-10">
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    {getOfferForSubscription('options_only').discount_text}
                  </div>
                  {getOfferForSubscription('options_only').remaining_slots && (
                    <div className="bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold mt-1 text-center">
                      {getOfferForSubscription('options_only').remaining_slots} left!
                    </div>
                  )}
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-golden text-xl">📘 OPTIONS ONLY (Educational Module)</CardTitle>
                <CardDescription className="text-gray-300">
                  Monthly Educational Sessions Based on Past Market Data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-golden mb-4">₹{fmtPrice(safePrices.options_only)}<span className="text-lg text-gray-400">/month</span></div>
                <ul className="space-y-2 text-gray-300 mb-6">
                  <li>• Educational review of past options market behaviour</li>
                  <li>• Learning sessions on how strike levels behaved historically</li>
                  <li>• Illustrative study of past market entry/exit zones purely for understanding</li>
                  <li>• Monthly Q&A for conceptual clarification (non-advisory)</li>
                </ul>
                <Button className="w-full bg-golden text-black hover:bg-golden-light transition-colors"
                  onClick={() => handleSubscribe('options_only', getOfferForSubscription('options_only')?.code)}>
                  {getOfferForSubscription('options_only') ? '🎁 Claim Offer & Subscribe' : '👉 Subscribe Now'}
                </Button>
                {getOfferForSubscription('options_only') && (
                  <p className="text-xs text-green-400 mt-2 text-center font-medium">
                    Use code: {getOfferForSubscription('options_only').code}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-3 italic text-center">
                  (For learning purposes only. No live trades, no tips, no recommendations.)
                </p>
              </CardContent>
            </Card>
            )}

            {/* Package 2 - Complete Package */}
            {enabledServices.complete_package && (
            <Card className="bg-gradient-to-b from-golden/5 to-gray-900/80 border-golden hover:border-golden-light transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-golden to-golden-light"></div>
              {/* Offer Badge */}
              {getOfferForSubscription('complete_package') ? (
                <div className="absolute top-3 right-3 z-10">
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    {getOfferForSubscription('complete_package').discount_text}
                  </div>
                  {getOfferForSubscription('complete_package').remaining_slots && (
                    <div className="bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold mt-1 text-center">
                      {getOfferForSubscription('complete_package').remaining_slots} left!
                    </div>
                  )}
                </div>
              ) : (
                <Badge className="absolute top-4 right-4 bg-golden text-black font-semibold">Most Popular</Badge>
              )}
              <CardHeader>
                <CardTitle className="text-golden text-xl">📘 COMPLETE PACKAGE (Educational Module)</CardTitle>
                <CardDescription className="text-gray-300">
                  Monthly Study Sessions Covering Options, Futures & Equity Based on Historical Data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-golden mb-4">₹{fmtPrice(safePrices.complete_package)}<span className="text-lg text-gray-400">/month</span></div>
                <ul className="space-y-2 text-gray-300 mb-6">
                  <li>• Educational market review across Options, Futures & Equity (past data only)</li>
                  <li>• Illustrative chart-based learning using historical price movements</li>
                  <li>• Conceptual study of how past entry/exit/target zones formed</li>
                  <li>• Monthly Q&A for theory clarification (non-advisory)</li>
                  <li>• Priority doubt-resolution related to learning topics</li>
                </ul>
                <Button className="w-full bg-golden text-black hover:bg-golden-light transition-colors"
                  onClick={() => handleSubscribe('complete_package', getOfferForSubscription('complete_package')?.code)}>
                  {getOfferForSubscription('complete_package') ? '🎁 Claim Offer & Subscribe' : '👉 Subscribe Now'}
                </Button>
                {getOfferForSubscription('complete_package') && (
                  <p className="text-xs text-green-400 mt-2 text-center font-medium">
                    Use code: {getOfferForSubscription('complete_package').code}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-3 italic text-center">
                  (Strictly educational. No stock tips, no signals, no personalized advice.)
                </p>
              </CardContent>
            </Card>
            )}

            {/* Package 3 - Equity Only */}
            {enabledServices.equity_only && (
            <Card className="bg-gradient-to-b from-black/80 to-gray-900/80 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-golden to-golden-light"></div>
              {/* Offer Badge */}
              {getOfferForSubscription('equity_only') && (
                <div className="absolute top-3 right-3 z-10">
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    {getOfferForSubscription('equity_only').discount_text}
                  </div>
                  {getOfferForSubscription('equity_only').remaining_slots && (
                    <div className="bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold mt-1 text-center">
                      {getOfferForSubscription('equity_only').remaining_slots} left!
                    </div>
                  )}
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-golden text-xl">📘 EQUITY ONLY (Educational Module)</CardTitle>
                <CardDescription className="text-gray-300">
                  Monthly Learning Sessions on Equity/Cash Market Using Past Market Examples
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-golden mb-4">₹{fmtPrice(safePrices.equity_only)}<span className="text-lg text-gray-400">/month</span></div>
                <ul className="space-y-2 text-gray-300 mb-6">
                  <li>• Educational study of historical equity market patterns</li>
                  <li>• Understanding how earlier price structures created potential zones</li>
                  <li>• Learning momentum concepts using past examples</li>
                  <li>• Monthly Q&A for conceptual support (non-advisory)</li>
                </ul>
                <Button className="w-full bg-golden text-black hover:bg-golden-light transition-colors"
                  onClick={() => handleSubscribe('equity_only', getOfferForSubscription('equity_only')?.code)}>
                  {getOfferForSubscription('equity_only') ? '🎁 Claim Offer & Subscribe' : '👉 Subscribe Now'}
                </Button>
                {getOfferForSubscription('equity_only') && (
                  <p className="text-xs text-green-400 mt-2 text-center font-medium">
                    Use code: {getOfferForSubscription('equity_only').code}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-3 italic text-center">
                  (For education only. No live market guidance, no actionable recommendations.)
                </p>
              </CardContent>
            </Card>
            )}
          </div>

          {/* Index Options (Educational Purpose Only) - Monthly */}
          {enabledServices.index_options_educational && (
          <div className="mt-12 max-w-md mx-auto">
            <Card className="bg-gradient-to-b from-black/80 to-gray-900/80 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-golden to-golden-light"></div>
              {/* Offer Badge */}
              {getOfferForSubscription('index_options_educational') && (
                <div className="absolute top-3 right-3 z-10">
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    {getOfferForSubscription('index_options_educational').discount_text}
                  </div>
                  {getOfferForSubscription('index_options_educational').remaining_slots && (
                    <div className="bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold mt-1 text-center">
                      {getOfferForSubscription('index_options_educational').remaining_slots} left!
                    </div>
                  )}
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-golden text-xl">📘 INDEX OPTIONS (Educational Purpose Only)</CardTitle>
                <CardDescription className="text-gray-300">
                  Monthly Educational Sessions Based on Past Index Options Market Data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-golden mb-4">₹{fmtPrice(safePrices.index_options_educational)}<span className="text-lg text-gray-400">/month</span></div>
                <ul className="space-y-2 text-gray-300 mb-6">
                  <li>• Educational review of past options market behaviour</li>
                  <li>• Learning sessions on how strike levels behaved historically</li>
                  <li>• Illustrative study of past market entry/exit zones purely for understanding</li>
                  <li>• Monthly Q&A for conceptual clarification (non-advisory)</li>
                </ul>
                <Button
                  data-testid="subscribe-index-options-educational-btn"
                  className="w-full bg-golden text-black hover:bg-golden-light transition-colors"
                  onClick={() => handleSubscribe('index_options_educational', getOfferForSubscription('index_options_educational')?.code)}>
                  {getOfferForSubscription('index_options_educational') ? '🎁 Claim Offer & Subscribe' : '👉 Subscribe Now'}
                </Button>
                {getOfferForSubscription('index_options_educational') && (
                  <p className="text-xs text-green-400 mt-2 text-center font-medium">
                    Use code: {getOfferForSubscription('index_options_educational').code}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-3 italic text-center">
                  (For learning purposes only. No live trades, no tips, no recommendations.)
                </p>
              </CardContent>
            </Card>
          </div>
          )}

          {/* 3-Month Plans */}
          <div className="mt-12">
            <div className="text-center mb-8">
              <Badge className="bg-green-600 text-white mb-4">3-Month Plans - Best Value!</Badge>
              <h3 className="text-2xl font-bold text-golden">Save More with Quarterly Subscriptions</h3>
              <p className="text-gray-300 mt-2">Get 10% discount on 3-month plans</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Options Only - 3 Months */}
              {enabledServices.options_only && (
              <Card className="bg-gradient-to-b from-green-900/30 to-gray-900/80 border-green-500/40 hover:border-green-400 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-green-400"></div>
                {/* Offer Badge */}
                {getOfferForSubscription('options_only_3month') ? (
                  <div className="absolute top-3 right-3 z-10">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      {getOfferForSubscription('options_only_3month').discount_text}
                    </div>
                    {getOfferForSubscription('options_only_3month').remaining_slots && (
                      <div className="bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold mt-1 text-center">
                        {getOfferForSubscription('options_only_3month').remaining_slots} left!
                      </div>
                    )}
                  </div>
                ) : (
                  <Badge className="absolute top-4 right-4 bg-green-600 text-white font-semibold">3 Months</Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-green-400 text-xl">📘 Options Only – 3 Months (Educational Plan)</CardTitle>
                  <CardDescription className="text-gray-300">
                    Quarterly program providing educational sessions based on past market data, focusing on Options concepts and historical chart behaviour.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-400 mb-2">₹{fmtPrice(safePrices.options_only_3month)}<span className="text-lg text-gray-400">/3 months</span></div>
                  <div className="text-sm text-green-300 mb-4">Save ₹1,050 compared to monthly!</div>
                  <p className="text-gray-300 text-sm font-semibold mb-2">You Will Receive (Educational Only):</p>
                  <ul className="space-y-2 text-gray-300 mb-6 text-sm">
                    <li>• 90 Days of Options Market Learning Sessions (based on historical charts)</li>
                    <li>• Strike Price Behaviour Study using past market examples</li>
                    <li>• Historical Case Studies on option movements</li>
                    <li>• Concept-based Learning Plan</li>
                    <li>• Student Support for Doubt-Resolution</li>
                  </ul>
                  <Button className="w-full bg-green-600 text-white hover:bg-green-500 transition-colors"
                    onClick={() => handleSubscribe('options_only_3month', getOfferForSubscription('options_only_3month')?.code)}>
                    {getOfferForSubscription('options_only_3month') ? '🎁 Claim Offer & Subscribe' : 'Subscribe Now'}
                  </Button>
                  {getOfferForSubscription('options_only_3month') && (
                    <p className="text-xs text-green-400 mt-2 text-center font-medium">
                      Use code: {getOfferForSubscription('options_only_3month').code}
                    </p>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Complete Package - 3 Months */}
              {enabledServices.complete_package && (
              <Card className="bg-gradient-to-b from-green-900/40 to-gray-900/80 border-green-400 hover:border-green-300 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-green-300"></div>
                {/* Offer Badge */}
                {getOfferForSubscription('complete_package_3month') ? (
                  <div className="absolute top-3 right-3 z-10">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      {getOfferForSubscription('complete_package_3month').discount_text}
                    </div>
                    {getOfferForSubscription('complete_package_3month').remaining_slots && (
                      <div className="bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold mt-1 text-center">
                        {getOfferForSubscription('complete_package_3month').remaining_slots} left!
                      </div>
                    )}
                  </div>
                ) : (
                  <Badge className="absolute top-4 right-4 bg-green-600 text-white font-semibold">Best Deal</Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-green-400 text-xl">📘 Complete Package – 3 Months (Educational Plan)</CardTitle>
                  <CardDescription className="text-gray-300">
                    Comprehensive quarterly program covering Options, Futures & Equity concepts using only historical examples and past data analysis.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-400 mb-2">₹{fmtPrice(safePrices.complete_package_3month)}<span className="text-lg text-gray-400">/3 months</span></div>
                  <div className="text-sm text-green-300 mb-4">Save ₹2,375 compared to monthly!</div>
                  <p className="text-gray-300 text-sm font-semibold mb-2">You Will Receive (Educational Only):</p>
                  <ul className="space-y-2 text-gray-300 mb-6 text-sm">
                    <li>• Options + Futures + Equity concept-based learning</li>
                    <li>• 90 Days of Historical Market Coverage</li>
                    <li>• Past Chart Analysis & Pattern Interpretation</li>
                    <li>• Understanding Entry/Exit Logic (theoretical only, no live or forward-looking guidance)</li>
                    <li>• Doubt-Resolution Support</li>
                    <li>• Priority Assistance</li>
                  </ul>
                  <Button className="w-full bg-green-600 text-white hover:bg-green-500 transition-colors"
                    onClick={() => handleSubscribe('complete_package_3month', getOfferForSubscription('complete_package_3month')?.code)}>
                    {getOfferForSubscription('complete_package_3month') ? '🎁 Claim Offer & Subscribe' : 'Subscribe Now'}
                  </Button>
                  {getOfferForSubscription('complete_package_3month') && (
                    <p className="text-xs text-green-400 mt-2 text-center font-medium">
                      Use code: {getOfferForSubscription('complete_package_3month').code}
                    </p>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Equity Only - 3 Months */}
              {enabledServices.equity_only && (
              <Card className="bg-gradient-to-b from-green-900/30 to-gray-900/80 border-green-500/40 hover:border-green-400 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-green-400"></div>
                {/* Offer Badge */}
                {getOfferForSubscription('equity_only_3month') ? (
                  <div className="absolute top-3 right-3 z-10">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      {getOfferForSubscription('equity_only_3month').discount_text}
                    </div>
                    {getOfferForSubscription('equity_only_3month').remaining_slots && (
                      <div className="bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold mt-1 text-center">
                        {getOfferForSubscription('equity_only_3month').remaining_slots} left!
                      </div>
                    )}
                  </div>
                ) : (
                  <Badge className="absolute top-4 right-4 bg-green-600 text-white font-semibold">3 Months</Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-green-400 text-xl">📘 Equity Only – 3 Months (Educational Plan)</CardTitle>
                  <CardDescription className="text-gray-300">
                    Quarterly education program focused on equity market concepts using past charts & historical market behaviour.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-400 mb-2">₹{fmtPrice(safePrices.equity_only_3month)}<span className="text-lg text-gray-400">/3 months</span></div>
                  <div className="text-sm text-green-300 mb-4">Save ₹1,500 compared to monthly!</div>
                  <p className="text-gray-300 text-sm font-semibold mb-2">You Will Receive (Educational Only):</p>
                  <ul className="space-y-2 text-gray-300 mb-6 text-sm">
                    <li>• 90 Days of Equity Market Learning Sessions (past market examples only)</li>
                    <li>• Concept-based Entry/Exit Logic Study (no actionable calls)</li>
                    <li>• Historical Trend & Momentum Case Studies</li>
                    <li>• Pattern Recognition Training (based on past data)</li>
                    <li>• Student Support for Queries</li>
                  </ul>
                  <Button className="w-full bg-green-600 text-white hover:bg-green-500 transition-colors"
                    onClick={() => handleSubscribe('equity_only_3month', getOfferForSubscription('equity_only_3month')?.code)}>
                    {getOfferForSubscription('equity_only_3month') ? '🎁 Claim Offer & Subscribe' : 'Subscribe Now'}
                  </Button>
                  {getOfferForSubscription('equity_only_3month') && (
                    <p className="text-xs text-green-400 mt-2 text-center font-medium">
                      Use code: {getOfferForSubscription('equity_only_3month').code}
                    </p>
                  )}
                </CardContent>
              </Card>
              )}
            </div>
          </div>
        </div>

          {/* Mandatory Disclaimer */}
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="bg-red-900/20 border-red-500/40">
              <CardHeader>
                <CardTitle className="text-red-400 text-center text-xl">✅ Mandatory Disclaimer</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-300">
                <p className="text-center font-semibold mb-4">
                  This service is strictly for educational purposes only.
                </p>
                <div className="space-y-2 mb-4">
                  <p className="font-semibold">We do not provide:</p>
                  <ul className="space-y-1 ml-4">
                    <li>❌ Stock recommendations</li>
                    <li>❌ Buy/Sell/Hold/Exit calls</li>
                    <li>❌ Targets, Stop-loss, or Live Trading Advice</li>
                    <li>❌ Portfolio or personalised investment guidance</li>
                  </ul>
                </div>
                <p className="text-center text-sm italic">
                  All content is based on historical market data and is meant solely for learning and research.
                </p>
              </CardContent>
            </Card>
          </div>

        
        {/* Automated Alerts Service */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">Educational Access</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Educational Telegram Alerts on Options (Based on Past Data)</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-6">
              Learn from historical options market behaviour through past case studies
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* 1 Day Plan */}
            {enabledServices.automated_alerts_1day && (
            <Card className="bg-gradient-to-b from-black/80 to-gray-900/80 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-golden to-golden-light"></div>
              <CardHeader>
                <CardTitle className="text-golden text-xl">1 DAY ACCESS</CardTitle>
                <CardDescription className="text-gray-300">
                  Educational access to past-options-learning modules for 1 trading day.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-golden mb-4">₹{fmtPrice(safePrices.automated_alerts_1day)}<span className="text-lg text-gray-400">/day</span></div>
                <ul className="space-y-2 text-gray-300 mb-6">
                  <li>• Historical Case Studies on Options Movement</li>
                  <li>• Learning Frameworks Explained</li>
                  <li>• Past Chart Behaviour Illustrations</li>
                  <li>• General Educational Notes</li>
                  <li>• Valid for 1 Day</li>
                </ul>
                <Button 
                  className="w-full bg-golden text-black hover:bg-golden-light transition-colors"
                  onClick={() => handleSubscribe('automated_alerts_1day')}
                >
                  Subscribe Now
                </Button>
              </CardContent>
            </Card>
            )}

            {/* 3 Days Plan */}
            {enabledServices.automated_alerts_3day && (
            <Card className="bg-gradient-to-b from-golden/5 to-gray-900/80 border-golden hover:border-golden-light transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-golden to-golden-light"></div>
              <Badge className="absolute top-4 right-4 bg-golden text-black font-semibold">Better Value</Badge>
              <CardHeader>
                <CardTitle className="text-golden text-xl">3 DAY ACCESS</CardTitle>
                <CardDescription className="text-gray-300">
                  Educational content for 3 days. No buy/sell calls.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-golden mb-4">₹{fmtPrice(safePrices.automated_alerts_3day)}<span className="text-lg text-gray-400">/3 days</span></div>
                <ul className="space-y-2 text-gray-300 mb-6">
                  <li>• Options Education via Past Market Examples</li>
                  <li>• Concept-Based Framework Discussions</li>
                  <li>• Historical Chart Review</li>
                  <li>• General Educational Notes</li>
                  <li>• Valid for 3 Days</li>
                  <li>• Better Value Plan</li>
                </ul>
                <Button 
                  className="w-full bg-golden text-black hover:bg-golden-light transition-colors"
                  onClick={() => handleSubscribe('automated_alerts_3day')}
                >
                  Subscribe Now
                </Button>
              </CardContent>
            </Card>
            )}

            {/* 5 Days Plan */}
            {enabledServices.automated_alerts_5day && (
            <Card className="bg-gradient-to-b from-black/80 to-gray-900/80 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-golden to-golden-light"></div>
              <Badge className="absolute top-4 right-4 bg-green-600 text-white font-semibold">Best Value</Badge>
              <CardHeader>
                <CardTitle className="text-golden text-xl">5 DAY ACCESS</CardTitle>
                <CardDescription className="text-gray-300">
                  Extended educational access for 5 days.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-golden mb-4">₹{fmtPrice(safePrices.automated_alerts_5day)}<span className="text-lg text-gray-400">/5 days</span></div>
                <ul className="space-y-2 text-gray-300 mb-6">
                  <li>• Options Learning through Chart History</li>
                  <li>• Framework-Based Teaching Sessions</li>
                  <li>• Historical Study Modules</li>
                  <li>• General Educational Notes</li>
                  <li>• Valid for 5 Days</li>
                  <li>• Best Value</li>
                </ul>
                <Button 
                  className="w-full bg-golden text-black hover:bg-golden-light transition-colors"
                  onClick={() => handleSubscribe('automated_alerts_5day')}
                >
                  Subscribe Now
                </Button>
              </CardContent>
            </Card>
            )}
          </div>
        </div>
      </section>

      {/* Upcoming Workshop Section */}
      <section id="workshop" className="py-16 px-4 bg-gray-900/30">
        <div className="container mx-auto max-w-3xl text-center">
          <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">Workshop</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Upcoming Physical <span className="text-golden">Workshop</span>
          </h2>
          <Card className="bg-gradient-to-r from-black/90 to-gray-900/90 border-golden/30">
            <CardContent className="py-12 px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-golden/10 border border-golden/30 flex items-center justify-center">
                <Calendar className="w-8 h-8 text-golden" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Stay Tuned</h3>
              <p className="text-gray-400 text-base max-w-md mx-auto">
                Details and pricing for the next physical workshop will be announced here. Follow us on social media to get notified first.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Reviews/Testimonials Section */}
      <ReviewsSection API_URL={API_URL} />

      {/* AI Track Record — only rendered if 30+ evaluated ideas AND 70%+ win rate */}
      <AITrackRecordSection API_URL={API_URL} />

      {/* Active Offers Section - Visible Coupons */}
      {activeOffers.length > 0 && (
        <section id="offers" className="py-16 px-4 bg-gradient-to-b from-black via-gray-900/50 to-black" data-testid="offers-section">
          <div className="container mx-auto">
            <div className="text-center mb-10">
              <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">Limited Time</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                Active <span className="text-golden">Offers</span>
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto">
                Use these coupon codes at checkout to get exclusive discounts
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {activeOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-golden/30 rounded-xl overflow-hidden group hover:border-golden/60 transition-all"
                  data-testid={`offer-card-${offer.code}`}
                >
                  {/* Banner Image */}
                  {offer.creative_image_url && (
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={offer.creative_image_url.startsWith('http') ? offer.creative_image_url : `${API_URL}${offer.creative_image_url}`}
                        alt={offer.offer_title || offer.code}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    {/* Discount Badge */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-golden text-black px-3 py-1 rounded-full text-sm font-bold">
                        {offer.discount_text}
                      </span>
                      {offer.remaining_slots !== null && (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          offer.remaining_slots <= 10
                            ? 'bg-red-500/20 text-red-400 animate-pulse'
                            : 'bg-green-500/15 text-green-400'
                        }`}>
                          {offer.remaining_slots} left
                        </span>
                      )}
                    </div>
                    {/* Title & Description */}
                    <h3 className="text-lg font-bold text-white mb-1">
                      {offer.offer_title || 'Special Offer'}
                    </h3>
                    {offer.offer_description && (
                      <p className="text-gray-400 text-sm mb-3">{offer.offer_description}</p>
                    )}
                    {/* Coupon Code */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-1 bg-black/50 border border-dashed border-golden/50 rounded-lg px-4 py-2.5 text-center">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Coupon Code</p>
                        <p className="text-golden text-lg font-mono font-bold tracking-widest">{offer.code}</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-golden hover:bg-golden/90 text-black font-bold px-4 h-12"
                        onClick={() => {
                          navigator.clipboard.writeText(offer.code);
                          const targetType = offer.applicable_subscription_types?.[0];
                          if (targetType) {
                            handleSubscribe(targetType, offer.code);
                          } else {
                            scrollToSection('services');
                          }
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                    {/* Applicable Services */}
                    {offer.applicable_subscription_types?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {offer.applicable_subscription_types.slice(0, 4).map((t, i) => (
                          <span key={i} className="text-[10px] text-gray-500 bg-white/5 px-2 py-0.5 rounded">
                            {t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                        ))}
                        {offer.applicable_subscription_types.length > 4 && (
                          <span className="text-[10px] text-gray-500">+{offer.applicable_subscription_types.length - 4} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">Connect with Me</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Stay Updated</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Get real-time updates on Indian Market, market breakdowns, and exclusive updates
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-5xl mx-auto">
            <a 
              href="https://twitter.com/ChiragR31538873" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer group">
                <CardContent className="p-6 text-center">
                  <Twitter className="w-12 h-12 text-golden mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <div className="font-semibold text-white mb-2">X (Twitter)</div>
                  <div className="text-golden text-sm">@ChiragR31538873</div>
                  <ExternalLink className="w-4 h-4 text-gray-400 mx-auto mt-2" />
                </CardContent>
              </Card>
            </a>
            
            <a 
              href="https://instagram.com/uppercircuitstocks" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer group">
                <CardContent className="p-6 text-center">
                  <Instagram className="w-12 h-12 text-golden mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <div className="font-semibold text-white mb-2">Instagram</div>
                  <div className="text-golden text-sm">@uppercircuitstocks</div>
                  <ExternalLink className="w-4 h-4 text-gray-400 mx-auto mt-2" />
                </CardContent>
              </Card>
            </a>
            
            <a 
              href="https://t.me/chiragrathod88" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer group">
                <CardContent className="p-6 text-center">
                  <Send className="w-12 h-12 text-golden mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <div className="font-semibold text-white mb-2">Telegram</div>
                  <div className="text-golden text-sm">@chiragrathod88</div>
                  <ExternalLink className="w-4 h-4 text-gray-400 mx-auto mt-2" />
                </CardContent>
              </Card>
            </a>
            
            <a 
              href="https://t.me/uppercircuitstocksbychiragrathod" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer group">
                <CardContent className="p-6 text-center">
                  <MessageCircle className="w-12 h-12 text-golden mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <div className="font-semibold text-white mb-2">Telegram Channel</div>
                  <div className="text-golden text-xs break-all px-2">@uppercircuitstocks</div>
                  <ExternalLink className="w-4 h-4 text-gray-400 mx-auto mt-2" />
                </CardContent>
              </Card>
            </a>

            <a 
              href="https://www.youtube.com/@UpperCircuitStocks" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-black/50 border-golden/20 hover:border-golden/40 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer group">
                <CardContent className="p-6 text-center">
                  <Youtube className="w-12 h-12 text-golden mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <div className="font-semibold text-white mb-2">YouTube</div>
                  <div className="text-golden text-sm">@UpperCircuitStocks</div>
                  <ExternalLink className="w-4 h-4 text-gray-400 mx-auto mt-2" />
                </CardContent>
              </Card>
            </a>
          </div>
          
          <div className="text-center mt-12">
            <h3 className="text-2xl font-bold text-golden mb-4">Ready to level up?</h3>
            <p className="text-lg text-gray-300 mb-6">DM us on socials to get started. Your next big win awaits!</p>
            <Button 
              size="lg" 
              className="bg-golden text-black hover:bg-golden-light transition-all duration-300 transform hover:scale-105 px-8 py-3 text-lg font-semibold"
            >
              Start Your Journey
              <TrendingUp className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/90 border-t border-golden/20 py-8 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Logo and Brand */}
            <div className="text-center md:text-left">
              <div className="flex justify-center md:justify-start items-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg" 
                  alt="Upper Circuit Stocks Logo" 
                  className="h-16 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-2">© 2024 Upper Circuit Stocks by Chirag Rathod. All rights reserved.</p>
              <p className="text-golden text-sm">Trade smarter, not harder.</p>
            </div>

            {/* Quick Links */}
            <div className="text-center md:text-left">
              <h4 className="text-golden font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-golden transition-colors">About</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-golden transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('workshop')} className="text-gray-400 hover:text-golden transition-colors">Workshop</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-golden transition-colors">Contact</button></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div className="text-center md:text-left">
              <h4 className="text-golden font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="/privacy-policy" 
                    className="text-gray-400 hover:text-golden transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a 
                    href="/cancellation-policy" 
                    className="text-gray-400 hover:text-golden transition-colors"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Cancellation & Refunds
                  </a>
                </li>
                <li>
                  <a 
                    href="/terms-and-conditions" 
                    className="text-gray-400 hover:text-golden transition-colors"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </a>
                </li>
                <li>
                  <span className="text-gray-400 text-sm">
                    Contact: chiragbrathod1988@gmail.com
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-golden/20 mt-8 pt-6 text-center">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-500 text-sm mb-2 md:mb-0">
                All trading and investment services carry risk. Past performance does not guarantee future results.
              </p>
              <div className="flex space-x-4 text-sm">
                <a href="/privacy-policy" className="text-gray-500 hover:text-golden transition-colors">Privacy</a>
                <span className="text-gray-600">|</span>
                <a href="/cancellation-policy" className="text-gray-500 hover:text-golden transition-colors">Refunds</a>
                <span className="text-gray-600">|</span>
                <a href="/terms-and-conditions" className="text-gray-500 hover:text-golden transition-colors">Terms</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button — provided globally by App.js */}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={closePaymentModal}
        subscriptionType={paymentModal.subscriptionType}
        packageDetails={paymentModal.packageDetails}
      />

    </div>
  );
};

// Reviews Section Component
const ReviewsSection = ({ API_URL }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Review form state
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewerName, setReviewerName] = useState('');
  const [reviewContent, setReviewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews?limit=6`);
      const data = await response.json();
      if (data.status === 'success') {
        setReviews(data.reviews);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setSubmitMessage('Please select a star rating');
      return;
    }
    if (!reviewerName.trim()) {
      setSubmitMessage('Please enter your name');
      return;
    }
    if (!reviewContent.trim() || reviewContent.length < 10) {
      setSubmitMessage('Please write at least 10 characters');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitMessage('');
      
      const response = await fetch(`${API_URL}/api/reviews/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          user_name: reviewerName.trim(),
          content: reviewContent.trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitMessage('✅ Thank you for your review!');
        setRating(0);
        setReviewerName('');
        setReviewContent('');
        setShowForm(false);
        // Refresh reviews to show new one if auto-approved
        setTimeout(fetchReviews, 1000);
      } else {
        setSubmitMessage(data.detail || 'Failed to submit review');
      }
    } catch (error) {
      setSubmitMessage('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (count, interactive = false) => {
    const displayRating = interactive ? (hoverRating || rating) : count;
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : undefined}
            onClick={interactive ? () => setRating(star) : undefined}
            onMouseEnter={interactive ? () => setHoverRating(star) : undefined}
            onMouseLeave={interactive ? () => setHoverRating(0) : undefined}
            className={`text-2xl transition-all duration-150 ${
              star <= displayRating ? 'text-golden' : 'text-gray-600'
            } ${interactive ? 'cursor-pointer hover:scale-110' : ''}`}
            disabled={!interactive}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  return (
    <section id="reviews" className="py-16 px-4 bg-gradient-to-b from-black/50 to-black/30">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">Testimonials</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Subscribers Say</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Real experiences from traders who've transformed their trading journey with us
          </p>
        </div>

        {/* Reviews Grid */}
        {!loading && reviews.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
            {reviews.map((review) => (
              <Card 
                key={review.id} 
                className="bg-black/50 border-golden/20 hover:border-golden/40 transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    <div className="text-lg">{renderStars(review.rating)}</div>
                    {review.is_featured && (
                      <Badge className="ml-2 bg-golden/20 text-golden text-xs">Featured</Badge>
                    )}
                  </div>
                  <p className="text-gray-300 text-sm mb-4">{review.content}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-golden/10">
                    <div className="font-medium text-golden">{review.user_name}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(review.created_at).toLocaleDateString('en-IN', { 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Write Review Section */}
        <div className="max-w-2xl mx-auto">
          {!showForm ? (
            <div className="text-center">
              <p className="text-gray-400 mb-4">Share your experience with us!</p>
              <Button 
                onClick={() => setShowForm(true)}
                className="bg-golden hover:bg-golden/90 text-black font-semibold"
              >
                ⭐ Write a Review
              </Button>
            </div>
          ) : (
            <Card className="bg-black/60 border-golden/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-golden mb-4 text-center">Write Your Review</h3>
                
                <form onSubmit={handleSubmitReview} className="space-y-5">
                  {/* Star Rating */}
                  <div className="text-center">
                    <label className="block text-gray-300 mb-2">Click to rate</label>
                    <div className="flex justify-center">
                      {renderStars(rating, true)}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {rating === 0 ? 'Select your rating' : 
                       rating === 5 ? 'Excellent!' : 
                       rating === 4 ? 'Very Good!' : 
                       rating === 3 ? 'Good' : 
                       rating === 2 ? 'Fair' : 'Poor'}
                    </p>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-gray-300 mb-2">Your Name</label>
                    <input
                      type="text"
                      value={reviewerName}
                      onChange={(e) => setReviewerName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-golden focus:outline-none"
                      maxLength={50}
                    />
                  </div>

                  {/* Review Content */}
                  <div>
                    <label className="block text-gray-300 mb-2">Your Review</label>
                    <textarea
                      value={reviewContent}
                      onChange={(e) => setReviewContent(e.target.value)}
                      placeholder="Share your experience..."
                      className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-golden focus:outline-none min-h-[100px] resize-none"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right">{reviewContent.length}/500</p>
                  </div>

                  {/* Message */}
                  {submitMessage && (
                    <p className={`text-center text-sm ${submitMessage.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>
                      {submitMessage}
                    </p>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-3 justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setSubmitMessage('');
                      }}
                      className="border-gray-600 text-gray-300 hover:bg-gray-800"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-golden hover:bg-golden/90 text-black font-semibold px-8"
                    >
                      {submitting ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
};

export default LandingPage;